import React, { useEffect, useState } from 'react';
import PreviewSubscriptionData from 'interface/PreviewSubscriptionData';
import { previewSubscriptionChange } from 'api/SubscriptionApiService';
import { useToast } from 'provider/ToastProvider';
import AccountType from 'interface/AccountType';
import { formatCentsToCurrency } from 'utils/formatCurrency';
import styles from 'styles/components/ProrationChargeCard.module.sass'
import PrimaryButton from 'components/common/PrimaryButton';

interface ProrationChargeCardProps {
    selectedAccount: AccountType;
    billingCycle: 'monthly' | 'annual';
    setnextInvoiceRecurringCents: (cents: number | null) => void;
    setShowInvoicePreview: (show: boolean) => void;
    setLoading: (loading: boolean) => void;
    setCurrentStep: (step: number) => void;
    onConfirm: () => void;
}

const failedCodes = ['NO_ACTIVE_SUBSCRIPTION', 'PLAN_NOT_FOUND', 'PREVIEW_FAILED', 'USER_NOT_FOUND'];

/**
 * ProrationChargeCard component displays a card showing proration charges
 * when a user changes their subscription plan.
 * 
 * @component
 * @param {ProrationChargeCardProps} props - The properties for the ProrationChargeCard component.
 * @param {AccountType} props.selectedAccount - The selected account type.
 * @param {'monthly' | 'annual'} props.billingCycle - The billing cycle (monthly or annual).
 * @param {(cents: number | null) => void} props.setnextInvoiceRecurringCents - Function to set the next invoice recurring cents.
 * @param {(show: boolean) => void} props.setShowInvoicePreview - Function to control the visibility of the invoice preview.
 * @param {(loading: boolean) => void} props.setLoading - Function to set the loading state.
 * @param {(step: number) => void} props.setCurrentStep - Function to set the current step in the process.
 * @param {() => void} props.onConfirm - Function to handle the confirmation action.
 * 
 * @returns {JSX.Element} The rendered ProrationChargeCard component.
 * 
 * @example
 * <ProrationChargeCard
 *  selectedAccount={{ name: 'pro', displayName: 'Pro Plan' }}
 *  billingCycle='monthly'
 *  setnextInvoiceRecurringCents={(cents) => console.log(cents)}
 *  setShowInvoicePreview={(show) => console.log(show)}
 *  setLoading={(loading) => console.log(loading)}
 *  setCurrentStep={(step) => console.log(step)}
 *  onConfirm={() => console.log('Confirmed')}
 * />
 */
const ProrationChargeCard: React.FC<ProrationChargeCardProps> = ({ selectedAccount, billingCycle, setnextInvoiceRecurringCents, setShowInvoicePreview, setLoading, onConfirm, setCurrentStep }) => {
    const [previewData, setPreviewData] = useState<PreviewSubscriptionData | null>(null);
    const [hasPreview, setHasPreview] = useState<boolean>(false);
    const [isNoChangeDetected, setIsNoChangeDetected] = useState<boolean>(false);
    const [isNoCharge, setIsNoCharge] = useState<boolean>(false);
    const [hasBreakdown, setHasBreakdown] = useState<boolean>(false);
    const [hasNextBillingInfo, setHasNextBillingInfo] = useState<boolean>(false);

    const { addToast } = useToast();

    const applyPreviewDefaults = (preview: PreviewSubscriptionData, codeVal: string | null) => {
        const nextIsNoCharge = (preview.amountDueCents ?? 0) <= 0;
        // const nextTitle = nextIsNoCharge ? 'No charge today' : 'Standard charge';

        const hasBreakdown =
            !!preview.breakdown &&
            !!(preview.breakdown.prorationDebit || preview.breakdown.prorationCredit || preview.breakdown.tax || preview.breakdown.discount || preview.breakdown.credit);

        const hasNextBillingInfo = !!(preview.nextBillingAnchorEpoch && preview.nextInvoiceRecurringCents);

        setIsNoCharge(nextIsNoCharge);
        // setTitle(nextTitle);
        setnextInvoiceRecurringCents(preview.nextInvoiceRecurringCents as number || null);
        setHasBreakdown(hasBreakdown);
        setHasNextBillingInfo(hasNextBillingInfo);
        setHasPreview(Boolean(!failedCodes.includes(codeVal as string)));
        setShowInvoicePreview(true);

        addToast({
            type: 'success',
            message: 'Invoice preview fetched successfully.'
        })
        setCurrentStep(2);
    }

    const renderAmount = (cents: number, currency: string, isNegative?: boolean) => {
        const sign = isNegative ? '-' : cents < 0 ? '-' : '';
        const absCents = Math.abs(cents);
        return `${sign}${formatCentsToCurrency({ cents: absCents, currency })}`;
    }

    const getNoChargeMessage = () => {
        if (!previewData) return '';
        const breakdown = previewData.breakdown;
        const due = previewData.amountDueCents ?? 0;

        const prorationCredit = breakdown?.prorationCredit?.amountCents as number ?? 0;
        const credit = breakdown?.credit?.amountCents as number ?? 0;
        const discount = breakdown?.discount?.amountCents as number ?? 0;

        const totalCreditsAbs = Math.abs(
            [prorationCredit, credit, discount]
                .filter(val => val < 0)
                .reduce((sum, val) => sum + val, 0)
        );

        if (selectedAccount.name === 'free') {
            return 'No charge today';
        }

        // 1) Pure proration fully credited
        if (previewData.hasProration && due === 0) {
            return 'No charge today (proration fully credited)';
        }

        // 2) Credits/discounts fully offset what would be due
        if (totalCreditsAbs >= due && due === 0) {
            return 'Credit applied, no charge today';
        }

        // 3) Partial credit/discount applied but there is still a charge
        if (totalCreditsAbs > 0 && due > 0) {
            return 'Credit applied (charge adjusted)';
        }

        // 4) Fallback for zero due without proration context
        if (due === 0) {
            return 'No charge today';
        }

        if (due > 0) {
            return 'Charge due today';
        }
        return 'No charge today';
    }

    useEffect(() => {
        const fetchPreview = async () => {
            setLoading(true);
            try {
                const response = await previewSubscriptionChange({
                    tag: 'ProrationChargeCard',
                    data: { accountType: selectedAccount.name, billingCycle }
                });
                const preview = response?.preview?.preview as PreviewSubscriptionData;
                const codeVal = response?.preview?.code as string;

                if (codeVal === 'NO_CHANGE_DETECTED') {
                    setIsNoChangeDetected(true);
                    setShowInvoicePreview(true);
                    setCurrentStep(2);
                    return;
                }

                if (!preview || !codeVal) {
                    addToast({
                        type: 'error',
                        message: 'Failed to fetch invoice preview. Please try again later.'
                    });
                    setHasPreview(false);
                    return;
                }
                setPreviewData(preview);

                // Apply defaults and show toast
                applyPreviewDefaults(preview, codeVal);
            } catch {
                addToast({
                    type: 'error',
                    message: 'Failed to fetch invoice preview. Please try again later.'
                });
                setHasPreview(false);
            } finally {
                setLoading(false);
            }
        };
        fetchPreview();
    }, [selectedAccount, billingCycle]);

    return (
        <>
            {isNoChangeDetected ? (
                <div className={styles['no-change-card']}>
                    <p className={styles['no-change-title']}>You&apos;re already on this plan</p>
                    <p className={styles['no-change-message']}>The selected plan and billing cycle match your current subscription. Go back and select a different plan to continue.</p>
                </div>
            ) : !previewData || !hasPreview ? (
                <div></div>
            ) : (
                <div className={styles['proration-charge-card']}>
                    {/* <p className={styles['section-title']}>{title}</p> */}
                    {/* Header Section */}
                    <div className={styles['section-header']}>
                        <div className={styles['header-content']}>
                            <p className={styles['header-label']}>
                                {previewData.label || 'Prorated charge'}
                            </p>
                            <div className={styles['amount-due']}>
                                <span className={styles['due-now-label']}>{ isNoCharge ? 'No charge today' : 'Due now'}</span>
                                <p className={styles['due-now-amount']} aria-label={`Amount due: ${renderAmount(previewData ? previewData.amountDueCents : 0, previewData.currency)}`}>
                                    {renderAmount(previewData.amountDueCents, previewData.currency)}
                                </p>
                                <p
                                    className={styles['no-charge-message']}>
                                    {getNoChargeMessage()}
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    {/* Basic Breakdown Section */}
                    <div className={styles['breakdown-section']}>
                        <div className={styles['divider']} />
                        <div className={styles['breakdown-list']} role={'list'} aria-label={'Breakdown of charges'}>
                            {previewData.breakdown?.prorationDebit && (
                                <div className={styles['breakdown-item']} role={'listitem'}>
                                    <span className={styles['breakdown-label']}>{previewData.breakdown.prorationDebit.label}</span>
                                    <span className={styles['breakdown-amount']}>{renderAmount(previewData.breakdown.prorationDebit.amountCents, previewData.currency)}</span>
                                </div>
                            )}
                            {previewData.breakdown?.prorationCredit?.amountCents && (
                                <div className={styles['breakdown-item']} role={'listitem'}>
                                    <span className={styles['breakdown-label']}>{previewData.breakdown.prorationCredit.label}</span>
                                    <span className={`${styles['breakdown-amount']} ${styles[`breakdown-amount--negative`]}`}>{renderAmount(previewData.breakdown.prorationCredit.amountCents, previewData.currency)}</span>
                                </div>
                            )}
                            {previewData.breakdown?.tax && (
                                <div className={styles['breakdown-item']} role={'listitem'}>
                                    <span className={styles['breakdown-label']}>Tax</span>
                                    <span className={styles['breakdown-amount']}>{renderAmount(previewData.breakdown.tax.amountCents, previewData.currency)}</span>
                                </div>
                            )}
                            {previewData.discountCents > 0 && (
                                <div className={styles['breakdown-item']} role={'listitem'}>
                                    <span className={styles['breakdown-label']}>Discount</span>
                                    <span className={styles['breakdown-amount']}>{renderAmount(previewData.discountCents, previewData.currency)}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Next Billing Info */}
                    {hasNextBillingInfo && (
                        <div className={styles['next-billing-section']}>
                            <div className={styles['divider']} />
                            <div className={styles['next-billing-info']}>
                                <h4 className={styles['next-billing-title']}>What happens next</h4>

                                {previewData.nextInvoiceRecurringCents !== undefined && previewData.nextBillingAnchorEpoch ? (
                                    <p className={styles['next-billing-text']}>
                                        Next renewal on{' '}
                                        <span className={styles['next-billing-highlight']}>
                                            {new Date(previewData.nextBillingAnchorEpoch * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                                        </span>{' '}
                                        at{' '}
                                        <span className={styles['next-billing-highlight']}>
                                            {renderAmount(previewData.nextInvoiceRecurringCents, previewData.currency)}
                                        </span>
                                    </p>
                                ) : previewData.nextInvoiceRecurringCents !== undefined ? (
                                    <p className={styles['next-billing-text']}>
                                        Next charge { ' '}
                                        <span className={styles['next-billing-highlight']}>
                                            {renderAmount(previewData.nextInvoiceRecurringCents, previewData.currency)}
                                        </span>
                                    </p>
                                ) : previewData.nextBillingAnchorEpoch ? (
                                    <p className={styles['next-billing-text']}>
                                        Renews on{' '}
                                        <span className={styles['next-billing-highlight']}>
                                            {new Date(previewData.nextBillingAnchorEpoch * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                                        </span>
                                    </p>
                                ) : null}
                            </div>
                        </div>
                    )}

                    {/* Detailed Breakdown Section */}
                    {hasBreakdown && (
                        <>
                        </>
                    )}

                    {/* Action Section */}
                    <div className={styles['action-section']}>
                        <PrimaryButton
                            text={isNoCharge ? 'No charge today' : 'Confirm and Pay Now'}
                            primaryColor='#ff5522'
                            textColor='#fff'
                            hoverColor='#fff'
                            hoverTextColor='#ff5522'
                            width='100%'
                            doSomething={onConfirm}
                        />
                    </div>
                </div>
            )}
        </>
    )
};

export default ProrationChargeCard;